import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { OlefinRoute } from '../../types';

/** 煤制天然气（CTG）P&ID 详细图：气化 → 变换 → 低温甲醇洗 → 两段甲烷化 → 干燥 */
const media = {
  coal: { label: '碎煤', light: '#4e342e', dark: '#a1887f', width: 2.6 },
  syngas: { label: '合成气 / 净化气', light: '#5c6bc0', dark: '#9fa8da', width: 2.6 },
  gas: { label: '天然气（SNG）', light: '#2e7d32', dark: '#81c784', width: 2.6 },
  oxy: { label: '气化剂（O₂+蒸汽）', light: '#d32f2f', dark: '#e57373', width: 2 },
  chem: { label: '低温甲醇', light: '#00838f', dark: '#4dd0e1', width: 2 },
  water: { label: '水', light: '#1565c0', dark: '#64b5f6', width: 2 },
};

const devices = [
  { id: 'r101', kind: 'reactor' as const, x: 180, y: 128, tag: 'R-101', name: '气化炉', note: '加压气化' },
  { id: 'r102', kind: 'reactor' as const, x: 370, y: 128, tag: 'R-102', name: '变换炉', note: 'CO+H₂O' },
  { id: 't101', kind: 'column' as const, x: 540, y: 94, tag: 'T-101', name: '低温甲醇洗', note: '脱硫脱碳' },
  { id: 'r103', kind: 'reactor' as const, x: 720, y: 128, tag: 'R-103', name: '甲烷化一', note: '~300℃' },
  { id: 'r104', kind: 'reactor' as const, x: 890, y: 128, tag: 'R-104', name: '甲烷化二' },
  { id: 'v101', kind: 'vDrum' as const, x: 1050, y: 161, tag: 'V-101', name: '产品气干燥' },
];

const ports: PidPort[] = [
  { id: 'p-coal', label: '碎煤', medium: 'coal', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-oxy', label: '气化剂（O₂+蒸汽）', medium: 'oxy', x: 130, y: 40, direction: 'in' as const },
  { id: 'p-co2', label: 'CO₂ / 含硫尾气', medium: 'chem', x: 620, y: 52, direction: 'out' as const },
  { id: 'p-gas', label: '天然气（SNG）', medium: 'gas', x: 1220, y: 224, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 's01', from: 'p-coal', fromSide: 'right', to: 'r101', toSide: 'left', medium: 'coal' },
  { id: 's02', from: 'p-oxy', fromSide: 'right', to: 'r101', toSide: 'top', medium: 'oxy' },
  { id: 's03', from: 'r101', fromSide: 'right', to: 'r102', toSide: 'left', medium: 'syngas' },
  { id: 's04', from: 'r102', fromSide: 'right', to: 't101', toSide: 'left', medium: 'syngas' },
  { id: 's05', from: 't101', fromSide: 'right', to: 'r103', toSide: 'left', medium: 'syngas' },
  { id: 's06', from: 'r103', fromSide: 'right', to: 'r104', toSide: 'left', medium: 'syngas' },
  { id: 's07', from: 'r104', fromSide: 'right', to: 'v101', toSide: 'left', medium: 'gas' },
  { id: 's08', from: 'v101', fromSide: 'right', to: 'p-gas', toSide: 'left', medium: 'gas' },
  { id: 's09', from: 't101', fromSide: 'top', to: 'p-co2', toSide: 'left', medium: 'chem' },
];

const CtgDiagram: React.FC<{ route: OlefinRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="煤制天然气（SNG）装置详细流程"
    subtitle="碎煤加压气化 → CO 变换 → 低温甲醇洗（脱硫脱碳）→ 两段甲烷化 → 产品气干燥，主流程一条直线贯通（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default CtgDiagram;
